/**
 * One person's lookback as two charts sharing an x-axis (concept §7, L2 user
 * view): page views as bars scaled by duration with the slow ones emphasised
 * and each exception as a mark, and underneath the Cato path metrics for the
 * same 15-minute buckets — TTFB, TLS and TCP as three lines.
 *
 * Two cards, not one dual-axis chart (§2 "never"): seconds of form load and
 * milliseconds of TCP connect share a clock, not a scale. The charts share a
 * numeric time domain and a Recharts `syncId`, so hovering one highlights the
 * same instant on the other.
 *
 * No network rows renders the explicit "not visible" state, not an empty axis
 * (§5). `http_*` is not plotted because it is not in the contract (D3).
 */

import { Chart } from "@norican/dataviz";
import { CircleAlert, Icon, TriangleAlert } from "@norican/icons";
import { EmptyState, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { FoConsoleLookback, FoConsoleUserView } from "@/lib/api";
import { identityConfig } from "@/lib/charts/config";

import {
  formatDurationCompact,
  formatDurationMs,
  lookbackOption,
  timelineDomain,
  UNATTRIBUTED_CHIP,
  viewRumPoints,
} from "./console-format";
import { PanelCard } from "./panel-card";
import { formatBinDateTime, formatBinTime, formatCount } from "./status";

/** Recharts `syncId` shared by the charts. */
const SYNC_ID = "fo-user-timeline";

/** Bar width in px for event bars on a numeric time axis. */
const EVENT_BAR_PX = 3;

const NETWORK_CONFIG = identityConfig(["ttfb_ms", "tls_ms", "tcp_ms"], {
  ttfb_ms: "TTFB",
  tls_ms: "TLS connect",
  tcp_ms: "TCP connect",
});

/** The browser's two series — the same ramp positions as the network chart's first two. */
const RUM_CONFIG = identityConfig(["ttfb_p50_ms", "long_task_ms"], {
  ttfb_p50_ms: "TTFB p50 (browser)",
  long_task_ms: "Main thread busy",
});

const PAGE_VIEW_CONFIG = {
  duration_ms: { label: "Form load", color: "var(--norican-chart-1)" },
  slow: { label: "Slow (record exists)", color: "var(--norican-warning)" },
};

interface UserTimelineProps {
  view: FoConsoleUserView;
  lookback: FoConsoleLookback;
  staleAfterMs: number;
  /** Open the §5 record for a slow page view. */
  onOpenRecord: (activityId: string) => void;
}

function parseTs(value: string): number | null {
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function UserTimeline({ view, lookback, staleAfterMs, onOpenRecord }: UserTimelineProps) {
  const domain = timelineDomain(lookback, view.collected_at);
  const longWindow = lookbackOption(lookback).ms > 24 * 3_600_000;
  const formatTick = (value: number) =>
    longWindow
      ? formatBinDateTime(new Date(value).toISOString())
      : formatBinTime(new Date(value).toISOString());

  const pageViews = view.timeline
    .map((pv) => ({ ...pv, x: parseTs(pv.ts) }))
    .filter((pv): pv is typeof pv & { x: number } => pv.x != null)
    .sort((a, b) => a.x - b.x);
  const exceptions = view.exceptions
    .map((exception) => ({ ...exception, x: parseTs(exception.ts) }))
    .filter((exception): exception is typeof exception & { x: number } => exception.x != null);
  const network = view.network
    .map((point) => ({ ...point, x: parseTs(point.bucket) }))
    .filter((point): point is typeof point & { x: number } => point.x != null)
    .sort((a, b) => a.x - b.x);

  const rum = viewRumPoints(view)
    .map((point) => ({ ...point, x: parseTs(point.bucket) }))
    .filter((point): point is typeof point & { x: number } => point.x != null)
    .sort((a, b) => a.x - b.x);

  const slowCount = pageViews.filter((pv) => pv.slow).length;
  const windowCaption = lookbackOption(lookback).caption;

  return (
    <div className="flex flex-col gap-4">
      <ChartCard<(typeof pageViews)[number]>
        title="Page views, scaled by duration"
        caption={`${formatCount(pageViews.length)} form loads, ${formatCount(slowCount)} slow, ${formatCount(
          exceptions.length,
        )} exceptions · ${windowCaption} · click a slow bar for its record`}
        since={view.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-user-page-views",
          ariaLabel: "Every page view in the lookback",
          data: pageViews,
          getRowId: (row) => row.activity_id,
          columns: [
            {
              id: "ts",
              header: "Time",
              accessor: (row) => row.ts,
              cell: (row) => <span className="tabular-nums">{formatBinDateTime(row.ts)}</span>,
            },
            { id: "form", header: "Form", accessor: (row) => row.form },
            { id: "entity", header: "Entity", accessor: (row) => row.legal_entity },
            {
              id: "duration",
              header: "Duration",
              align: "end",
              accessor: (row) => row.duration_ms,
              cell: (row) => (
                <span className="tabular-nums">{formatDurationMs(row.duration_ms)}</span>
              ),
            },
            { id: "aos", header: "AOS", accessor: (row) => row.aos },
            { id: "slow", header: "Slow", accessor: (row) => (row.slow ? "yes" : "no") },
          ],
        }}
      >
        {pageViews.length === 0 ? (
          <EmptyState
            size="sm"
            title="No page views in this lookback"
            description="This person opened no F&O form in the window. Nothing was slow, because nothing was measured."
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Chart.Container
              config={PAGE_VIEW_CONFIG}
              className="h-64"
              aria-label={`Form loads by time for this person, ${windowCaption}, bar height is load duration`}
            >
              <Chart.ComposedChart
                data={pageViews}
                syncId={SYNC_ID}
                syncMethod="value"
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis
                  {...Chart.axisProps}
                  dataKey="x"
                  type="number"
                  scale="time"
                  domain={domain}
                  allowDataOverflow
                  tickFormatter={formatTick}
                />
                <Chart.YAxis
                  {...Chart.axisProps}
                  width={48}
                  tickFormatter={(value: number) => formatDurationCompact(value)}
                />
                <Chart.Tooltip
                  cursor={{ fill: "var(--norican-surface-hover)" }}
                  content={
                    <Chart.TooltipContent
                      labelFormatter={(label) =>
                        typeof label === "number"
                          ? formatBinDateTime(new Date(label).toISOString())
                          : label
                      }
                      rows={(datum: (typeof pageViews)[number]) => [
                        { label: "Form", value: datum.form },
                        { label: "Entity", value: datum.legal_entity },
                        { label: "Duration", value: formatDurationMs(datum.duration_ms) },
                        { label: "AOS", value: datum.aos },
                        { label: "Slow", value: datum.slow ? "yes — record exists" : "no" },
                      ]}
                    />
                  }
                />
                {exceptions.map((exception) => (
                  <Chart.ReferenceLine
                    key={`${exception.activity_id}-${exception.ts}`}
                    x={exception.x}
                    stroke="var(--norican-danger)"
                    strokeDasharray="2 2"
                    ifOverflow="extendDomain"
                  />
                ))}
                <Chart.Bar
                  {...Chart.seriesDefaults}
                  dataKey="duration_ms"
                  barSize={EVENT_BAR_PX}
                  radius={[2, 2, 0, 0]}
                  cursor="pointer"
                  onClick={(_, index) => {
                    const pv = pageViews[index];
                    if (pv?.slow) {
                      onOpenRecord(pv.activity_id);
                    }
                  }}
                >
                  {pageViews.map((pv) => (
                    <Chart.Cell
                      key={pv.activity_id}
                      fill={pv.slow ? "var(--color-slow)" : "var(--color-duration_ms)"}
                    />
                  ))}
                </Chart.Bar>
              </Chart.ComposedChart>
            </Chart.Container>
            {/* Emphasis and exception marks are status, so they carry words. */}
            <div className="text-fg-muted flex flex-wrap justify-center gap-4 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <Icon icon={TriangleAlert} size="xs" aria-hidden="true" />
                Slow — a §5 record exists
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Icon icon={CircleAlert} size="xs" aria-hidden="true" />
                Dashed line — an F&O exception at that instant
              </span>
            </div>
          </div>
        )}
      </ChartCard>

      <ChartCard<(typeof network)[number]>
        title="Network path (Cato, 15-minute buckets)"
        caption={
          network.length === 0
            ? UNATTRIBUTED_CHIP
            : `${formatCount(network.length)} buckets with a Cato row · ${windowCaption} · TCP, TLS and TTFB are measured without TLS inspection`
        }
        since={view.collected_at}
        staleAfterMs={staleAfterMs}
        tableTwin={{
          id: "fo-user-network",
          ariaLabel: "Every 15-minute network bucket in the lookback",
          data: network,
          getRowId: (row) => row.bucket,
          columns: [
            {
              id: "bucket",
              header: "Bucket",
              accessor: (row) => row.bucket,
              cell: (row) => <span className="tabular-nums">{formatBinDateTime(row.bucket)}</span>,
            },
            { id: "site", header: "Site", accessor: (row) => row.site },
            { id: "src", header: "Source", accessor: (row) => row.src_type },
            { id: "pop", header: "PoP", accessor: (row) => row.pop },
            { id: "isp", header: "ISP", accessor: (row) => row.isp },
            { id: "device", header: "Device", accessor: (row) => row.device },
            {
              id: "tcp",
              header: "TCP",
              align: "end",
              accessor: (row) => row.tcp_ms,
              cell: (row) => <span className="tabular-nums">{formatDurationMs(row.tcp_ms)}</span>,
            },
            {
              id: "tls",
              header: "TLS",
              align: "end",
              accessor: (row) => row.tls_ms,
              cell: (row) => <span className="tabular-nums">{formatDurationMs(row.tls_ms)}</span>,
            },
            {
              id: "ttfb",
              header: "TTFB",
              align: "end",
              accessor: (row) => row.ttfb_ms,
              cell: (row) => <span className="tabular-nums">{formatDurationMs(row.ttfb_ms)}</span>,
            },
            {
              id: "flows",
              header: "Flows",
              align: "end",
              accessor: (row) => row.flows,
              cell: (row) => <span className="tabular-nums">{formatCount(row.flows)}</span>,
            },
          ],
        }}
      >
        {network.length === 0 ? (
          <EmptyState
            size="sm"
            title="Network layer not visible for this user"
            description="Cato saw no flow from this person's mail in the lookback — they sit behind a site without internet breakout through Cato (DKTAA, CZPRI) or off the Cato Client. This is a coverage gap, not a clean path: peer comparison is the only network evidence."
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Chart.Container
              config={NETWORK_CONFIG}
              className="h-56"
              aria-label={`TTFB, TLS and TCP connect per 15-minute bucket for this person, ${windowCaption}`}
            >
              <Chart.LineChart
                data={network}
                syncId={SYNC_ID}
                syncMethod="value"
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis
                  {...Chart.axisProps}
                  dataKey="x"
                  type="number"
                  scale="time"
                  domain={domain}
                  allowDataOverflow
                  tickFormatter={formatTick}
                />
                <Chart.YAxis
                  {...Chart.axisProps}
                  width={48}
                  tickFormatter={(value: number) => formatDurationCompact(value)}
                />
                <Chart.Tooltip
                  content={
                    <Chart.TooltipContent
                      labelFormatter={(label) =>
                        typeof label === "number"
                          ? formatBinDateTime(new Date(label).toISOString())
                          : label
                      }
                      rows={(datum: (typeof network)[number]) => [
                        { label: "TTFB", value: formatDurationMs(datum.ttfb_ms) },
                        { label: "TLS connect", value: formatDurationMs(datum.tls_ms) },
                        { label: "TCP connect", value: formatDurationMs(datum.tcp_ms) },
                        { label: "Site", value: `${datum.site} (${datum.src_type})` },
                        { label: "PoP · ISP", value: `${datum.pop} · ${datum.isp}` },
                        { label: "Device", value: datum.device },
                        { label: "Flows", value: formatCount(datum.flows) },
                      ]}
                    />
                  }
                />
                <Chart.Legend content={<Chart.LegendContent />} />
                {(["ttfb_ms", "tls_ms", "tcp_ms"] as const).map((key) => (
                  <Chart.Line
                    key={key}
                    {...Chart.seriesDefaults}
                    dataKey={key}
                    type="monotone"
                    stroke={`var(--color-${key})`}
                    strokeWidth={2}
                    // A bucket with no value is a gap, never a drop to zero.
                    connectNulls={false}
                    dot={{ r: 2 }}
                  />
                ))}
              </Chart.LineChart>
            </Chart.Container>
            <Typography variant="caption" tone="muted">
              A bucket with no Cato row is a gap in the lines, not zero latency. Device CPU, RAM and
              Wi-Fi live in the Cato Experience Monitoring pages, not in this API.
            </Typography>
          </div>
        )}
      </ChartCard>

      {rum.length === 0 ? (
        <PanelCard
          title="Browser (RUM extension, 15-minute buckets)"
          caption="no browser telemetry"
        >
          <Typography variant="body-sm" tone="muted">
            No browser telemetry for this person in the lookback — the fo-rum extension is not
            installed or not reporting. Server wait, main-thread time and endpoint RTT are unknown,
            not zero.
          </Typography>
        </PanelCard>
      ) : (
        <ChartCard<(typeof rum)[number]>
          title="Browser (RUM extension, 15-minute buckets)"
          caption={`${formatCount(rum.length)} buckets with a browser block on a slow interaction · ${windowCaption} · the view carries no separate RUM series, so buckets without a record are gaps`}
          since={view.collected_at}
          staleAfterMs={staleAfterMs}
          tableTwin={{
            id: "fo-user-rum",
            ariaLabel: "Every 15-minute browser-telemetry bucket in the lookback",
            data: rum,
            getRowId: (row) => row.bucket,
            columns: [
              {
                id: "bucket",
                header: "Bucket",
                accessor: (row) => row.bucket,
                cell: (row) => (
                  <span className="tabular-nums">{formatBinDateTime(row.bucket)}</span>
                ),
              },
              {
                id: "requests",
                header: "Requests",
                align: "end",
                accessor: (row) => row.requests,
                cell: (row) => <span className="tabular-nums">{formatCount(row.requests)}</span>,
              },
              {
                id: "ttfb",
                header: "TTFB p50",
                align: "end",
                accessor: (row) => row.ttfb_p50_ms,
                cell: (row) => (
                  <span className="tabular-nums">{formatDurationMs(row.ttfb_p50_ms)}</span>
                ),
              },
              {
                id: "server",
                header: "Waiting on F&O",
                align: "end",
                accessor: (row) => row.server_ms,
                cell: (row) => (
                  <span className="tabular-nums">{formatDurationMs(row.server_ms)}</span>
                ),
              },
              {
                id: "long_task",
                header: "Main thread busy",
                align: "end",
                accessor: (row) => row.long_task_ms,
                cell: (row) => (
                  <span className="tabular-nums">{formatDurationMs(row.long_task_ms)}</span>
                ),
              },
              {
                id: "rtt",
                header: "RTT",
                align: "end",
                accessor: (row) => row.rtt_ms,
                cell: (row) => <span className="tabular-nums">{formatDurationMs(row.rtt_ms)}</span>,
              },
              { id: "conn", header: "Connection", accessor: (row) => row.conn_type || "—" },
              { id: "browser", header: "Browser", accessor: (row) => row.browser || "—" },
            ],
          }}
        >
          <div className="flex flex-col gap-2">
            <Chart.Container
              config={RUM_CONFIG}
              className="h-56"
              aria-label={`Browser-measured TTFB p50 and main-thread busy time per 15-minute bucket for this person, ${windowCaption}`}
            >
              <Chart.LineChart
                data={rum}
                syncId={SYNC_ID}
                syncMethod="value"
                margin={{ top: 8, right: 12, bottom: 0, left: 0 }}
              >
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis
                  {...Chart.axisProps}
                  dataKey="x"
                  type="number"
                  scale="time"
                  domain={domain}
                  allowDataOverflow
                  tickFormatter={formatTick}
                />
                <Chart.YAxis
                  {...Chart.axisProps}
                  width={48}
                  tickFormatter={(value: number) => formatDurationCompact(value)}
                />
                <Chart.Tooltip
                  content={
                    <Chart.TooltipContent
                      labelFormatter={(label) =>
                        typeof label === "number"
                          ? formatBinDateTime(new Date(label).toISOString())
                          : label
                      }
                      rows={(datum: (typeof rum)[number]) => [
                        { label: "TTFB p50 (browser)", value: formatDurationMs(datum.ttfb_p50_ms) },
                        { label: "Main thread busy", value: formatDurationMs(datum.long_task_ms) },
                        { label: "Waiting on F&O", value: formatDurationMs(datum.server_ms) },
                        { label: "RTT", value: formatDurationMs(datum.rtt_ms) },
                        { label: "Connection", value: datum.conn_type || "—" },
                        { label: "Requests", value: formatCount(datum.requests) },
                        { label: "Browser", value: datum.browser || "—" },
                      ]}
                    />
                  }
                />
                <Chart.Legend content={<Chart.LegendContent />} />
                {(["ttfb_p50_ms", "long_task_ms"] as const).map((key) => (
                  <Chart.Line
                    key={key}
                    {...Chart.seriesDefaults}
                    dataKey={key}
                    type="monotone"
                    stroke={`var(--color-${key})`}
                    strokeWidth={2}
                    // A bucket the browser did not post is a gap, never a zero.
                    connectNulls={false}
                    dot={{ r: 2 }}
                  />
                ))}
              </Chart.LineChart>
            </Chart.Container>
            <Typography variant="caption" tone="muted">
              Posted by this person&apos;s own browser (host, form and entity only — no titles, no
              form data). A bucket with no post is a gap in the lines, not zero.
            </Typography>
          </div>
        </ChartCard>
      )}
    </div>
  );
}
